<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    persistent
    no-click-animation
    max-width="720"
    :z-index="100000"
    content-class="portal-update-dialog"
    scrim="rgba(0, 0, 0, 0.55)"
  >
    <v-card class="portal-update-card" elevation="12" rounded="lg">
      <div class="portal-update-header">
        <img
          src="@/assets/rcs_logo_v4.png"
          alt="RCS Consulting Solutions"
          class="portal-update-logo"
        />
      </div>

      <div class="portal-update-body">
        <div class="portal-update-content">
          <article v-if="document && hasDocumentContent" class="release-notes-doc">
            <h2 class="rn-title">{{ document.title }}</h2>
            <p v-if="document.date" class="rn-date">
              {{ document.date }}
            </p>

            <template
              v-for="(block, blockIndex) in document.blocks"
              :key="`rn-block-${blockIndex}`"
            >
              <div v-if="block.type === 'highlight'" class="rn-highlight">
                <div
                  v-for="(entry, entryIndex) in highlightEntries(block)"
                  :key="`rn-hl-entry-${blockIndex}-${entryIndex}`"
                  class="rn-highlight-entry"
                >
                  <h3 v-if="entry.title" class="rn-highlight-title">
                    {{ entry.title }}
                  </h3>
                  <p
                    v-for="(para, paraIndex) in entry.paragraphs"
                    :key="`rn-hl-${blockIndex}-${entryIndex}-${paraIndex}`"
                    class="rn-highlight-text"
                    v-html="formatNoteHtml(para)"
                  ></p>
                </div>
              </div>

              <section v-else-if="block.type === 'section'" class="rn-section">
                <h3 v-if="block.title" class="rn-section-title">
                  {{ block.title }}
                </h3>
                <ul v-if="block.items && block.items.length" class="rn-list">
                  <li
                    v-for="(item, itemIndex) in block.items"
                    :key="`rn-item-${blockIndex}-${itemIndex}`"
                    class="rn-item"
                    :class="{
                      'rn-item--plain': !item.label,
                      'rn-item--inline': item.label && !item.hasDash,
                      'rn-item--footnote': item.footnote,
                    }"
                  >
                    <template v-if="item.hasDash">
                      <span class="rn-item-label">{{ item.label }}</span>
                      <span class="rn-item-dash" aria-hidden="true">—</span>
                      <span
                        class="rn-item-text"
                        v-html="formatNoteHtml(item.text)"
                      ></span>
                    </template>
                    <span
                      v-else
                      class="rn-item-text"
                      v-html="formatItemHtml(item)"
                    ></span>
                  </li>
                </ul>
              </section>
            </template>
          </article>
          <pre v-else-if="rawNotes" class="release-notes-fallback">{{ rawNotes }}</pre>
        </div>

        <div class="portal-update-footer">
          <p v-if="footerMessage" class="portal-update-message">
            {{ footerMessage }}
          </p>
          <v-btn
            class="portal-update-btn"
            color="#0066CC"
            size="large"
            rounded="lg"
            elevation="4"
            block
            :loading="busy"
            :disabled="busy"
            @click="onPrimaryClick"
          >
            {{ primaryLabel }}
            <v-icon v-if="variant === 'refresh'" end size="22">mdi-refresh</v-icon>
            <v-icon v-else-if="variant === 'browse'" end size="22">mdi-close</v-icon>
            <v-icon v-else end size="22">mdi-check</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ReleaseNote",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    /** refresh = git/deploy popup; acknowledge = viewed_release_notes popup; browse = dashboard latest notes */
    variant: {
      type: String,
      default: "refresh",
    },
    /** When set, use this text instead of fetching ReleaseNotes.txt */
    notes: {
      type: String,
      default: "",
    },
    busy: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "refresh", "acknowledge", "back"],
  data() {
    return {
      rawNotes: "",
    };
  },
  computed: {
    document() {
      return this.parseReleaseNotesDocument(this.rawNotes);
    },
    hasDocumentContent() {
      const doc = this.document;
      if (!doc) return false;
      return (doc.blocks || []).length > 0;
    },
    primaryLabel() {
      if (this.variant === "acknowledge") return "OK";
      if (this.variant === "browse") return "CLOSE";
      return "REFRESH";
    },
    footerMessage() {
      if (this.variant === "acknowledge" || this.variant === "browse") return "";
      return "Please refresh the page to continue with the latest version.";
    },
  }, //
  watch: {
    notes: {
      immediate: true,
      handler(value) {
        if (value) this.rawNotes = value;
      },
    },
    modelValue: {
      immediate: true,
      handler(open) {
        if (!open) return;
        if (this.notes) {
          this.rawNotes = this.notes;
          return;
        }
        // Same git-deployed ReleaseNotes.txt used by the refresh dialog
        this.fetchReleaseNotes();
      },
    },
  },
  methods: {
    onPrimaryClick() {
      if (this.variant === "acknowledge") {
        this.$emit("acknowledge");
        return;
      }
      if (this.variant === "browse") {
        this.$emit("back");
        this.$emit("update:modelValue", false);
        return;
      }
      this.$emit("refresh");
    },
    appAssetUrl(fileName) {
      const base = process.env.BASE_URL || "/";
      const normalizedBase = base.endsWith("/") ? base : `${base}/`;
      return `${normalizedBase}${fileName}`;
    },
    isUsableNotesText(text) {
      const trimmed = String(text || "").trim();
      if (!trimmed) return false;
      const lower = trimmed.toLowerCase();
      if (
        lower.startsWith("<!doctype") ||
        lower.startsWith("<html") ||
        lower.startsWith("<?xml") ||
        lower.startsWith("<error")
      ) {
        return false;
      }
      return true;
    },
    async fetchNotesFile(fileName) {
      const url = `${this.appAssetUrl(fileName)}?t=${Date.now()}`;
      const res = await fetch(url, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });
      if (!res.ok) return "";
      const trimmed = (await res.text() || "").trim();
      return this.isUsableNotesText(trimmed) ? trimmed : "";
    },
    async fetchReleaseNotes() {
      try {
        const text = await this.fetchNotesFile("ReleaseNotes.txt");
        if (text) this.rawNotes = text;
      } catch (error) {
        // Dialog still opens; content stays empty until the file is available
      }
    },
    looksLikeDate(line) {
      const text = String(line || "").trim();
      if (!text) return false;
      if (
        text.startsWith(":::") ||
        text.startsWith("##") ||
        text.startsWith("- ") ||
        this.matchFieldLabel(text)
      ) {
        return false;
      }
      return (
        /\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/i.test(
          text
        ) || /\b20\d{2}\b/.test(text)
      );
    },
    matchFieldLabel(line) {
      const text = String(line || "").trim();
      const match = text.match(
        /^(HIGHLIGHT TITLE|HIGHLIGHT TEXT|NORMAL TITLE|NORMAL TEXT)\s*:(.*)$/i
      );
      if (!match) return null;
      return {
        key: match[1].toUpperCase().replace(/\s+/g, "_"),
        rest: String(match[2] || "").trim(),
      };
    },
    highlightEntries(block) {
      if (Array.isArray(block?.entries) && block.entries.length) {
        return block.entries;
      }
      if (block?.title || (block?.paragraphs || []).length) {
        return [
          {
            title: block.title || "",
            paragraphs: block.paragraphs || [],
          },
        ];
      }
      return [];
    },
    isEmptyHighlight(block) {
      if (!block) return true;
      const entries = this.highlightEntries(block);
      return !entries.some((entry) => {
        const hasTitle = Boolean(String(entry.title || "").trim());
        const hasText = (entry.paragraphs || []).some((para) =>
          String(para || "").trim()
        );
        return hasTitle || hasText;
      });
    },
    isEmptySection(block) {
      if (!block) return true;
      const hasTitle = Boolean(String(block.title || "").trim());
      const hasItems = (block.items || []).length > 0;
      return !hasTitle && !hasItems;
    },
    splitItem(line) {
      const raw = String(line || "").trim();
      if (/^\*[^*\s]/.test(raw)) {
        return { label: "", text: raw, hasDash: false, footnote: true };
      }
      const cleaned = String(line || "")
        .replace(/^[-*•]\s+/, "")
        .replace(/^\d+\.\s+/, "")
        .trim();
      const dash = cleaned.indexOf(" — ");
      if (dash > 0) {
        return {
          label: cleaned.slice(0, dash).trim(),
          text: cleaned.slice(dash + 3).trim(),
          hasDash: true,
        };
      }
      const prefix = cleaned.match(/^(Fixed|Added|Improved):\s+(.*)$/i);
      if (prefix) {
        return {
          label: `${prefix[1]}:`,
          text: prefix[2],
          hasDash: false,
        };
      }
      return { label: "", text: cleaned, hasDash: false };
    },
    parseReleaseNotesDocument(raw) {
      const text = typeof raw === "string" ? raw.trim() : "";
      if (!text) return null;

      const lines = text.split(/\r?\n/);
      let index = 0;
      let title = "RCS Platform Release Notes";
      const firstLine = (lines[index] || "").trim();
      if (firstLine && !this.matchFieldLabel(firstLine)) {
        title = firstLine;
        index += 1;
      }

      let date = "";
      if (this.looksLikeDate(lines[index])) {
        date = (lines[index] || "").trim();
        index += 1;
      }

      const blocks = [];
      let currentHighlight = null;
      let currentSection = null;
      let highlightBuffer = [];

      const currentHighlightEntry = () => {
        const entries = currentHighlight?.entries || [];
        return entries[entries.length - 1] || null;
      };

      const flushHighlightParagraphs = () => {
        const paragraph = highlightBuffer.join(" ").trim();
        const entry = currentHighlightEntry();
        if (paragraph && entry) {
          entry.paragraphs.push(paragraph);
        }
        highlightBuffer = [];
      };

      const pushHighlight = () => {
        flushHighlightParagraphs();
        if (currentHighlight && !this.isEmptyHighlight(currentHighlight)) {
          blocks.push(currentHighlight);
        }
        currentHighlight = null;
      };

      const pushSection = () => {
        if (currentSection && !this.isEmptySection(currentSection)) {
          blocks.push(currentSection);
        }
        currentSection = null;
      };

      const startHighlight = (name = "") => {
        pushSection();
        flushHighlightParagraphs();
        if (!currentHighlight) {
          currentHighlight = {
            type: "highlight",
            entries: [],
          };
        }
        currentHighlight.entries.push({
          title: String(name || "").trim(),
          paragraphs: [],
        });
      };

      const startSection = (name = "") => {
        pushHighlight();
        pushSection();
        currentSection = {
          type: "section",
          title: String(name || "").trim(),
          items: [],
        };
      };

      while (index < lines.length) {
        const rawLine = lines[index] || "";
        const trimmed = rawLine.trim();
        const field = this.matchFieldLabel(trimmed);

        if (field) {
          if (field.key === "HIGHLIGHT_TITLE") {
            startHighlight(field.rest);
            index += 1;
            continue;
          }
          if (field.key === "HIGHLIGHT_TEXT") {
            if (!currentHighlight) startHighlight("");
            else {
              pushSection();
            }
            flushHighlightParagraphs();
            if (field.rest) highlightBuffer.push(field.rest);
            index += 1;
            continue;
          }
          if (field.key === "NORMAL_TITLE") {
            startSection(field.rest);
            index += 1;
            continue;
          }
          if (field.key === "NORMAL_TEXT") {
            if (!currentSection) startSection("");
            else {
              pushHighlight();
            }
            if (field.rest) currentSection.items.push(this.splitItem(field.rest));
            index += 1;
            continue;
          }
        }

        if (!trimmed) {
          if (currentHighlight) flushHighlightParagraphs();
          index += 1;
          continue;
        }

        if (trimmed === ":::highlight") {
          index += 1;
          const highlightTitle = (lines[index] || "").trim();
          index += 1;
          startHighlight(highlightTitle);
          while (index < lines.length && lines[index].trim() !== ":::") {
            if (!lines[index].trim()) flushHighlightParagraphs();
            else highlightBuffer.push(lines[index].trim());
            index += 1;
          }
          pushHighlight();
          if (lines[index] && lines[index].trim() === ":::") index += 1;
          continue;
        }

        if (trimmed.startsWith("## ")) {
          startSection(trimmed.slice(3).trim());
          index += 1;
          continue;
        }

        if (currentHighlight) {
          highlightBuffer.push(trimmed);
          index += 1;
          continue;
        }

        if (!currentSection) startSection("");
        currentSection.items.push(this.splitItem(trimmed));
        index += 1;
      }

      pushHighlight();
      pushSection();

      const highlight =
        blocks.find((block) => block.type === "highlight") || null;
      const sections = blocks.filter((block) => block.type === "section");

      return { title, date, highlight, sections, blocks };
    },
    formatNoteHtml(text) {
      const escaped = String(text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      return escaped.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    },
    formatItemHtml(item) {
      const label = item?.label
        ? `<strong>${this.formatNoteHtml(item.label)} </strong>`
        : "";
      return `${label}${this.formatNoteHtml(item?.text)}`;
    },
  },
};
</script>

<style>
.v-overlay:has(.portal-update-dialog),
.v-overlay:has(.portal-update-card) {
  z-index: 100000 !important;
}

body.portal-update-open .help-window,
body.portal-update-open .ai-chatbot-root,
body.portal-update-open .chat-fab,
body.portal-update-open .chat-window {
  z-index: 1000 !important;
  pointer-events: none !important;
}

.portal-update-dialog {
  overflow: visible !important;
  z-index: 100000 !important;
}

.portal-update-card {
  overflow: hidden;
  background: #ffffff !important;
  border-radius: 16px !important;
  display: flex;
  flex-direction: column;
  max-height: min(92vh, 860px);
}

.portal-update-header {
  flex-shrink: 0;
  background: #0b1f3a;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 32px;
  min-height: 84px;
}

.portal-update-logo {
  height: 64px;
  width: auto;
  max-width: 320px;
  object-fit: contain;
  mix-blend-mode: screen;
}

.portal-update-body {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  text-align: left;
  padding: 0;
  background: #ffffff;
  overflow: hidden;
}

.portal-update-content {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 28px 36px 12px;
  text-align: left;
  scrollbar-width: thin;
  scrollbar-color: rgba(11, 31, 58, 0.28) transparent;
}

.portal-update-content::-webkit-scrollbar {
  width: 6px;
}

.portal-update-content::-webkit-scrollbar-track {
  background: transparent;
}

.portal-update-content::-webkit-scrollbar-thumb {
  background-color: rgba(0, 102, 204, 0.3);
  border-radius: 6px;
}

.portal-update-content::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 102, 204, 0.5);
}

.portal-update-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  flex-shrink: 0;
  padding: 12px 36px 22px;
  background: #ffffff;
  border-top: 1px solid rgba(11, 31, 58, 0.08);
}

.release-notes-doc {
  color: #111111;
}

.release-notes-fallback {
  margin: 0;
  white-space: pre-wrap;
  color: #0b1f3a;
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.55;
}

.rn-title {
  margin: 0;
  color: #0b1f3a;
  font-size: 1.85rem;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.01em;
}

.rn-date {
  margin: 8px 0 20px;
  color: #a68244;
  font-size: 1.05rem;
  font-weight: 600;
}

.rn-highlight {
  background: #fcf4e7;
  border: 1px solid #d8bc8a;
  border-radius: 4px;
  padding: 16px 18px 14px;
  margin: 0 0 28px;
}

.rn-highlight-entry + .rn-highlight-entry {
  margin-top: 16px;
}

.rn-highlight-title {
  margin: 0 0 10px;
  color: #0b1f3a;
  font-size: 1.05rem;
  font-weight: 800;
}

.rn-highlight-text {
  margin: 0 0 10px;
  color: #111111;
  font-size: 0.95rem;
  line-height: 1.55;
}

.rn-highlight-entry .rn-highlight-text:last-child {
  margin-bottom: 0;
}

.rn-highlight-text strong {
  font-weight: 700;
}

.rn-section {
  margin: 0 0 22px;
}

.rn-section-title {
  margin: 0 0 10px;
  padding-bottom: 8px;
  color: #0b1f3a;
  font-size: 1.12rem;
  font-weight: 800;
  border-bottom: 1px solid #0b1f3a;
}

.rn-list {
  margin: 0;
  padding: 6px 0 0;
  list-style: none;
}

.rn-item {
  display: grid;
  grid-template-columns: 10px 12.5rem 1.2rem minmax(0, 1fr);
  column-gap: 8px;
  align-items: start;
  margin: 0 0 14px;
  color: #0b1f3a;
  font-size: 0.95rem;
  line-height: 1.5;
}

.rn-item::before {
  content: "";
  width: 7px;
  height: 7px;
  margin-top: 0.52em;
  border-radius: 50%;
  background: #c4a35a;
}

.rn-item-label {
  font-weight: 700;
  color: #0b1f3a;
}

.rn-item-dash {
  color: #0b1f3a;
  text-align: center;
  font-weight: 400;
}

.rn-item-text {
  color: #0b1f3a;
  font-weight: 400;
}

.rn-item--plain .rn-item-text,
.rn-item--inline .rn-item-text {
  grid-column: 2 / -1;
}

.rn-item--footnote {
  display: block;
}

.rn-item--footnote::before {
  content: none;
}

.portal-update-message {
  margin: 0 auto 12px;
  color: #4a5568;
  font-size: 0.88rem;
  line-height: 1.45;
  text-align: center;
}

.portal-update-btn {
  position: relative;
  z-index: 1;
  width: 100%;
  margin: 0 auto;
  height: 48px !important;
  font-weight: 700 !important;
  letter-spacing: 0.08em !important;
  color: #ffffff !important;
  border-radius: 12px !important;
  box-shadow: 0 10px 22px rgba(0, 102, 204, 0.35) !important;
}
</style>
